import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { trackEvent } from '../../../lib/analytics';

import { useShortKeysNavigation } from '../../../hooks/useShortKeysNavigation';

import { toggleBtnModalShortcuts } from '../../../redux/diagram/diagram.actions';

import Notify from '../../../components/Notify/Notify';
import ModalProfileMenu from '../../../components/Modals/ModalProfileMenu/ModalProfileMenu';
import ModalWorkspaceMenu from '../../../components/Modals/ModalWorkspaceMenu/ModalWorkspaceMenu';
import ModalPricingPlanLimit from '../../../components/Modals/ModalPricingPlanLimit/ModalPricingPlanLimit';
import ModalPricingPlanUpgrade from '../../../components/Modals/ModalPricingPlanUpgrade/ModalPricingPlanUpgrade';
import ModalInviteToApp from '../../../components/Modals/ModalInvitePeople/ModalInvitePeople';
import ModalFeedbackThanks from '../../../components/Modals/ModalFeedbackThanks/ModalFeedbackThanks';
import ModalSupport from '../../../components/Modals/ModalFeedback/ModalFeedback';

import ModalWorkspaceCreateNew from '../../../components/Modals/ModalWorkspaceCreateNew/ModalWorkspaceCreateNew';
import ModalWorkspaceLeave from '../../../components/Modals/ModalWorkspaceLeave/ModalWorkspaceLeave';
import ModalWorkspaceDelete from '../../../components/Modals/ModalWorkspaceDelete/ModalWorkspaceDelete';
import ModalMemberRemove from '../../../components/Modals/ModalMemberRemove/ModalMemberRemove';
import ModalInviteRevoke from '../../../components/Modals/ModalInviteRevoke/ModalInviteRevoke';
import ModalKeyboardShortcuts from '../../../components/Modals/ModalKeyboardShortcuts/ModalKeyboardShortcuts';

import SidebarSettings from '../_components/SidebarSettings/SidebarSettings';
import Header from '../_components/Header/Header';

import SettingsUsage from './_sections/SettingsUsage';
import SettingsBilling from './_sections/SettingsBilling';
import SettingsWorkspace from './_sections/SettingsWorkspace';
import SettingsMembers from './_sections/SettingsMembers';
import SettingsProfile from './_sections/SettingsProfile';
import SettingsPreferences from './_sections/SettingsPreferences';

import '../MainApp.scss';
import './Settings.scss';

const SECTIONS = ['usage', 'billing', 'workspace', 'members', 'profile', 'preferences'];

const SECTION_TABS = [
    { id: 'usage', label: 'Usage' },
    { id: 'billing', label: 'Billing' },
    { id: 'workspace', label: 'Workspace' },
    { id: 'profile', label: 'Profile' },
    { id: 'preferences', label: 'Preferences' },
];

// i18n keys (not literal strings) — resolved through t() where used (document.title).
const SECTION_TITLE_KEYS = {
    usage: 'settings_page.usage.doc_title',
    billing: 'settings_page.billing.header',
    workspace: 'settings_page.workspace.header',
    members: 'settings_page.members.header',
    profile: 'm_profile_menu.profile',
    preferences: 'dropdown_ctrl_settings.preferences',
};

const renderSection = (section) => {
    switch (section) {
        case 'usage': return <SettingsUsage />;
        case 'billing': return <SettingsBilling />;
        case 'workspace': return <SettingsWorkspace />;
        case 'members': return <SettingsMembers />;
        case 'profile': return <SettingsProfile />;
        case 'preferences': return <SettingsPreferences />;
        default: return null;
    }
};

const Settings = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { section } = useParams();

    const { isMac } = useShortKeysNavigation();

    const activeSection = SECTIONS.includes(section) ? section : 'usage';
    const [selectedType, setSelectedType] = useState(activeSection);

    // Keep the rendered section in sync with the URL — the sidebar uses NavLinks
    // that change the `:section` param without going through handleSelectTab.
    useEffect(() => {
        setSelectedType(activeSection);
    }, [activeSection]);

    useEffect(() => {
        const titleKey = SECTION_TITLE_KEYS[selectedType];
        const sectionTitle = titleKey ? t(titleKey) : t('b.settings');
        document.title = `${t('b.settings')} - ${sectionTitle}`;
    }, [selectedType, t, i18n.language]);

    useEffect(() => {
        // Block browser defaults for these combos (Ctrl/Cmd-only and Ctrl/Cmd+Shift).
        // Keystrokes here are claimed by the app — they should not trigger bookmarks,
        // view-source, downloads, DevTools, etc. while the settings page is active.
        const ctrlStopCodes = new Set([
            'KeyQ', 'KeyY', 'KeyU', 'KeyI', 'KeyJ', 'Quote', 'KeyB',
            'KeyS', 'KeyP', 'KeyL', 'KeyG', 'KeyH',
            // Reserved for future app shortcuts:
            'KeyO', 'KeyM', 'Period', 'Comma', 'KeyF', 'KeyK',
        ]);
        const ctrlShiftStopCodes = new Set(['KeyI', 'KeyC']);

        const handleKeyDown = (e) => {
            const isCtrl = isMac ? e.metaKey : e.ctrlKey;

            if (isCtrl && e.shiftKey && (e.code === 'KeyO' || e.key.toLowerCase() === 'o')) {
                e.preventDefault();
                return;
            }

            if (isCtrl && e.code === 'Slash') {
                e.preventDefault();
                dispatch(toggleBtnModalShortcuts());
                return;
            }

            if (isCtrl && e.shiftKey && ctrlShiftStopCodes.has(e.code)) {
                e.preventDefault();
                return;
            }

            if (isCtrl && !e.shiftKey && ctrlStopCodes.has(e.code)) {
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dispatch, isMac]);

    const handleSelectTab = (id) => {
        trackEvent(`app_settings_select_${id}`, { page: 'app', type: 'settings' });
        if (id !== selectedType) {
            setSelectedType(id);
            history.replace(`/settings/${id}/`);
        }
    };

    return (
        <div className="dash-container settings-page">
            <SidebarSettings />
            <div className='dash-main'>

                <Notify viewType='main' />
                <ModalProfileMenu />
                <ModalWorkspaceMenu />
                <ModalPricingPlanLimit />
                <ModalPricingPlanUpgrade />
                <ModalInviteToApp />
                <ModalFeedbackThanks />
                <ModalSupport />

                <ModalWorkspaceCreateNew />
                <ModalWorkspaceLeave />
                <ModalWorkspaceDelete />
                <ModalMemberRemove />
                <ModalInviteRevoke />

                <ModalKeyboardShortcuts />

                {/* <Header title='Settings' noHideIcon={true} /> */}

                <div className='dash-main-body'>
                    {/* <div className='selection-group'>
                        {SECTION_TABS.map(tab => (
                            <div
                                key={tab.id}
                                className={`selection-item ${selectedType === tab.id ? 'selected' : ''}`}
                                onClick={() => handleSelectTab(tab.id)}
                            >
                                {tab.label}
                            </div>
                        ))}
                    </div> */}

                    <div className='settings-section-content' key={selectedType}>
                        {renderSection(selectedType)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
